import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { ArrowRight, Lock } from "lucide-react";
import Logo from "@/components/Logo";

export default function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/leads", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] px-5 py-12">
      <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <Logo variant="full" />
          <div className="overline">Admin Console</div>
        </div>

        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight leading-tight">
          Sign in to<br />
          <span className="italic font-normal text-[var(--text-2)]">manage</span>{" "}
          <span className="text-[var(--vermilion)]">your studio.</span>
        </h1>

        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="mt-10 border border-[var(--border-dim)] bg-[var(--bg-2)] rounded-sm p-7 space-y-5"
        >
          <div>
            <label className="overline block mb-2">Email</label>
            <input
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@ksdevarc.com"
              className="w-full bg-[var(--bg)]/60 border border-[var(--border-dim)] rounded-sm px-4 py-3 font-mono-ui text-sm focus:outline-none focus:border-[var(--vermilion)] transition-colors"
            />
          </div>
          <div>
            <label className="overline block mb-2">Password</label>
            <input
              data-testid="admin-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full bg-[var(--bg)]/60 border border-[var(--border-dim)] rounded-sm px-4 py-3 font-mono-ui text-sm focus:outline-none focus:border-[var(--vermilion)] transition-colors"
            />
          </div>

          {error && (
            <div
              data-testid="admin-login-error"
              className="border border-[var(--vermilion)]/40 bg-[var(--vermilion)]/10 rounded-sm px-4 py-3 font-mono-ui text-[11px] tracking-wider text-[var(--vermilion)]"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[var(--vermilion)] hover:bg-[var(--vermilion-2)] disabled:opacity-60 text-white text-sm font-medium tracking-wide rounded-full transition-colors"
          >
            {loading ? "Signing in..." : "Sign in"}
            <ArrowRight className="w-4 h-4" />
          </button>

          <div className="pt-2 flex items-center justify-center gap-2 font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-3)]">
            <Lock className="w-3 h-3" /> Admin-only access
          </div>
        </form>
      </div>
    </div>
  );
}
